import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { ActiveItemCache } from 'scaleflow/utils/active-item-cache';
import { v4 as uuidv4 } from 'uuid';

export default class ListListItemComponent extends Component {
  @service store;
  @service router;
  @service('current-user') currentUser;
  @service('editor-active') editorActiveService;
  @service confetti;
  @service betaToggle;

  @tracked _activeComment = new ActiveItemCache();
  @tracked showComments = false;

  get isChecked() {
    return this.args.item.completed;
  }

  get visibleBadges() {
    return this.args.visibleBadges ?? [];
  }

  uniqueId = `quill-${uuidv4()}`;

  get editorActive() {
    if (!this.isInteractive) return false;
    return this.itemActive && this._activeComment.get() === undefined;
  }

  get isInteractive() {
    return this.args.interactive !== false;
  }

  get routeIsApproach() {
    return this.router.currentRouteName?.includes('approach');
  }

  get showTeamsBadge() {
    return (
      (this.visibleBadges?.includes('teams') && this.betaToggle.showCheckin) ||
      false
    );
  }

  get showDelayBadge() {
    return this.visibleBadges?.includes('delay') || false;
  }

  get showInitiativesBadge() {
    return this.visibleBadges?.includes('initiatives') || false;
  }

  get showWorkstreamsBadge() {
    return this.visibleBadges?.includes('workstreams') || false;
  }

  get showWeeksBadge() {
    if (!this.betaToggle.showPlanning) return false;
    return this.visibleBadges?.includes('weeks');
  }

  get itemActive() {
    if (this.routeIsApproach) {
      if (['wfa', 'uncertainty'].includes(this.args.type)) {
        if (this.args.item.assignees.length < 2) {
          return this.args.item.id == this.editorActiveService.activeItem;
        } else {
          return this.editorActiveService.activeEditorId === this.uniqueId;
        }
      }
      if (this.args.item.assignees.length >= 2)
        return this.editorActiveService.activeEditorId === this.uniqueId;

      //is planned and is not other means will appear in two spots atleast
      if (
        this.args.item.get('week').isTruthy &&
        this.args.item.get('workstream').isTruthy
      ) {
        return this.editorActiveService.activeEditorId === this.uniqueId;
      } else return this.args.item.id == this.editorActiveService.activeItem;
    }

    // else if(){}
    if (this.args.item.assignees.length < 2) {
      return this.args.item.id == this.editorActiveService.activeItem;
    } else {
      return this.editorActiveService.activeEditorId === this.uniqueId;
    }
  }

  get activeComment() {
    return this._activeComment.get();
  }

  get initiativeTags() {
    const initiative = this.args.item.get('initiative.id');
    const tags = this.checkForInitiativeTags(this.args.item.text_delta);
    if (initiative) return [initiative];
    return tags;
  }

  get parentType() {
    return this.args.item.constructor.modelName;
  }

  @action
  async saveItem() {
    await this.args.item.save();
  }

  @action
  openComments() {
    this.newComment();
    this.showComments = true;
  }

  get itemType() {
    return this.args.item.get('constructor.modelName');
  }

  @action
  async newComment() {
    let comments = (await this.args.item.comments) || [];
    // debugger;
    let new_and_empty_items = comments
      .filter((item) => item['isNew'] === true)
      .filter((item) => item['text_delta'] == undefined);

    if (new_and_empty_items.length == 0) {
      let itemType = this.args.item.get('constructor.modelName');
      const parentType = this.args.parent.constructor.modelName;
      const parentItem = this.args.parent;
      let new_comment = this.store.createRecord('comment', {
        [itemType]: this.args.item,
        [parentType]: parentItem,
        author: await this.currentUser.getActiveUser(),
      });
      this.activateComment(new_comment);
    } else {
      this.activateComment(new_and_empty_items[new_and_empty_items.length - 1]);
    }
  }

  @action
  activateComment(comment) {
    this._activeComment.set(comment);
    this.editorActiveService.setActiveEditor(this.uniqueId);
    this.editorActiveService.setActiveItem(this.args.item.id);
    this.args?.onActivate?.(this.uniqueId, this.args.item);
  }

  @action
  onDelete() {
    this.args.item?.destroyRecord();
  }

  @action
  toggleCheckbox() {
    if (!this.args.item.completed) {
      this.confetti.play();
    }
  }

  @action
  onActivate() {
    this.editorActiveService.setActiveEditor(this.uniqueId);
    this.editorActiveService.setActiveItem(this.args.item.id);
    this.activateComment(undefined);
    this.closeComments();
    if (this.args.onActivate) {
      this.args.onActivate(this.uniqueId, this.args.item);
    }
  }

  @action
  closeComments() {
    this.showComments = false;
  }

  @action
  deactivate() {
    if (!this.itemActive) return;
    this.editorActiveService.clearActiveEditor();
    this.closeComments();
    this.args?.deactivate?.(this.args.item);
  }

  @action
  async handleTeamSelect(team) {
    this.args.item.team = team;
    if (this.args.parentType === 'workstream') {
      const weeks = await this.args.parent.get('initiative.weeks');
      const current_week = weeks.slice().filter((item) => item.isCurrentWeek);
      if (current_week.length !== 0) this.args.item.week = current_week[0];
    }
    this.saveItem();
  }

  @action
  async handleInitiativeSelect(initiative) {
    const belongsToInitiative = this.args.item.initiative?.content;
    if (belongsToInitiative?.id === initiative.id) return;
    this.args.item.initiative = initiative;
    const weeks = initiative.weeks.content;
    const current_week = weeks.filter((item) => item.isCurrentWeek);
    if (current_week.length !== 0) this.args.item.week = current_week[0];
    else this.args.item.week = null;
    this.args.item.workstream = null;
    this.saveItem();
  }

  @action
  handleWorkstreamSelect(workstream) {
    this.args.item.workstream = workstream;
    this.saveItem();
  }

  @action
  handleAssigneesSelect(assignees) {
    this.args.item.assignees = assignees;
    this.saveItem();
  }

  @action
  handleWeekSelect(week) {
    this.args.item.week = week;
    this.saveItem();
  }

  checkForInitiativeTags(text_delta) {
    let arr = [];
    if (!text_delta) return;
    for (let i = 0; i < text_delta.ops.length; i++) {
      const item = text_delta.ops[i];
      if (item?.insert?.['initiative-tag'])
        arr.push(item.insert['initiative-tag'].id);
    }
    return arr;
  }

  @action
  clearTeam() {
    this.args.item.team = null;
    this.saveItem();
  }

  @action
  clearWeek() {
    this.args.item.week = null;
    this.saveItem();
  }

  @action
  clearWorkstream() {
    this.args.item.workstream = null;
    this.saveItem();
  }

  @action
  clearInitiative() {
    this.args.item.workstream = null;
    this.args.item.week = null;
    this.args.item.initiative = null;
    this.saveItem();
  }

  @action
  async toggleCompletedStatus() {
    this.args.item.completed = !this.isChecked;
    if (this.args.item.completed === true) this.confetti.play();
    await this.saveItem();
  }

  @action
  moveFocusUpByOneItem() {
    this.editorActiveService.moveFocusUpByOneItem();
  }

  @action
  moveFocusDownByOneItem() {
    this.editorActiveService.moveFocusDownByOneItem();
  }
}
